import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Download, Calendar, Users, Package, Layers, Search, Filter, Printer, ThermometerSun, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { formatDateTime, formatDate } from '@/utils/dateFormat';

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

// ======================== ABA: COBRANÇA ========================
function CobrancaTab() {
  const [relatorio, setRelatorio] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [filters, setFilters] = useState({
    data_inicio: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    data_fim: new Date().toISOString().split('T')[0],
    cliente_id: ''
  });

  useEffect(() => {
    fetchClientes();
    fetchRelatorio();
  }, []);

  const fetchClientes = async () => {
    try {
      const response = await api.get('/clientes');
      setClientes(response.data);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const fetchRelatorio = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
      if (filters.data_fim) params.append('data_fim', filters.data_fim);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);
      
      const response = await api.get(`/relatorios/cobranca?${params.toString()}`);
      setRelatorio(response.data);
    } catch (error) {
      toast.error('Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => { fetchRelatorio(); };

  const handleDownloadPDF = async () => {
    try {
      toast.info('Gerando PDF...');
      const params = new URLSearchParams();
      if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
      if (filters.data_fim) params.append('data_fim', filters.data_fim);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);
      const response = await api.get(`/relatorios/cobranca/pdf?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio-cobranca-${filters.data_inicio}-a-${filters.data_fim}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF baixado com sucesso!');
    } catch (error) { toast.error('Erro ao gerar PDF'); }
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
      if (filters.data_fim) params.append('data_fim', filters.data_fim);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);
      const response = await api.get(`/relatorios/cobranca/excel?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio-cobranca-${filters.data_inicio}-a-${filters.data_fim}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel baixado com sucesso!');
    } catch (error) { toast.error('Erro ao gerar Excel'); }
  };

  const chartData = relatorio?.clientes?.map(c => ({
    nome: c.cliente_nome.length > 15 ? c.cliente_nome.substring(0, 15) + '...' : c.cliente_nome,
    ciclos: c.total_ciclos,
    itens: c.total_itens,
    kits: c.total_kits
  })) || [];

  const pieData = relatorio?.clientes?.map(c => ({
    name: c.cliente_nome.length > 12 ? c.cliente_nome.substring(0, 12) + '...' : c.cliente_nome,
    value: c.total_ciclos
  })) || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-base text-slate-600">Resumo de serviços por cliente para faturamento</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExportExcel} className="bg-green-600 hover:bg-green-700" data-testid="export-excel-btn">
            <Download className="w-4 h-4 mr-2" />
            Exportar Excel
          </Button>
          <Button onClick={handleDownloadPDF} className="bg-red-600 hover:bg-red-700" data-testid="export-pdf-btn">
            <Download className="w-4 h-4 mr-2" />
            Exportar PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Filter className="w-5 h-5 text-blue-600" />
          Filtros
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="cb_data_inicio">Data Início</Label>
            <Input id="cb_data_inicio" type="date" value={filters.data_inicio} onChange={(e) => setFilters({ ...filters, data_inicio: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label htmlFor="cb_data_fim">Data Fim</Label>
            <Input id="cb_data_fim" type="date" value={filters.data_fim} onChange={(e) => setFilters({ ...filters, data_fim: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label htmlFor="cb_cliente">Cliente (opcional)</Label>
            <select id="cb_cliente" value={filters.cliente_id} onChange={(e) => setFilters({ ...filters, cliente_id: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm">
              <option value="">Todos os clientes</option>
              {clientes.map(c => (<option key={c.id} value={c.id}>{c.nome}</option>))}
            </select>
          </div>
          <div className="flex items-end">
            <Button onClick={handleFilter} className="w-full" data-testid="filter-cobranca-btn">
              <Search className="w-4 h-4 mr-2" />
              Filtrar
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {relatorio && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg"><Users className="w-6 h-6 text-blue-600" /></div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Clientes</p>
                <p className="text-2xl font-bold text-slate-900">{relatorio.totais.total_clientes}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg"><FileText className="w-6 h-6 text-green-600" /></div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Ciclos</p>
                <p className="text-2xl font-bold text-slate-900">{relatorio.totais.total_ciclos}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg"><Package className="w-6 h-6 text-purple-600" /></div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Itens</p>
                <p className="text-2xl font-bold text-slate-900">{relatorio.totais.total_itens}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg"><Layers className="w-6 h-6 text-orange-600" /></div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Kits</p>
                <p className="text-2xl font-bold text-slate-900">{relatorio.totais.total_kits}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      {relatorio && chartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Consumo por Cliente
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" stroke="#64748B" fontSize={12} />
                <YAxis dataKey="nome" type="category" width={100} stroke="#64748B" fontSize={11} />
                <Tooltip contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E2E8F0', borderRadius: '8px' }} />
                <Legend />
                <Bar dataKey="ciclos" fill="#2563EB" name="Ciclos" radius={[0, 4, 4, 0]} />
                <Bar dataKey="itens" fill="#10B981" name="Itens" radius={[0, 4, 4, 0]} />
                <Bar dataKey="kits" fill="#F59E0B" name="Kits" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Distribuição de Ciclos por Cliente
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} outerRadius={100} fill="#8884d8" dataKey="value">
                  {pieData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Client Details Table */}
      {relatorio && relatorio.clientes.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900">Detalhamento por Cliente</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Cliente</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">CNPJ/CPF</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Ciclos</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Itens</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Kits</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Aprovados</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Reprovados</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {relatorio.clientes.map((cliente) => (
                  <tr key={cliente.cliente_id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{cliente.cliente_nome}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{cliente.cliente_cnpj_cpf}</td>
                    <td className="px-6 py-4 text-sm text-center font-semibold text-blue-600">{cliente.total_ciclos}</td>
                    <td className="px-6 py-4 text-sm text-center font-semibold text-purple-600">{cliente.total_itens}</td>
                    <td className="px-6 py-4 text-sm text-center font-semibold text-orange-600">{cliente.total_kits}</td>
                    <td className="px-6 py-4 text-sm text-center">
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">{cliente.ciclos_aprovados}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-center">
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">{cliente.ciclos_reprovados}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {relatorio && relatorio.clientes.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-600">Nenhum dado encontrado</h3>
          <p className="text-sm text-slate-500 mt-2">Ajuste os filtros e tente novamente</p>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}
    </div>
  );
}


// ======================== ABA: AUTOCLAVES ========================
function AutoclavesTab() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    data_inicio: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    data_fim: new Date().toISOString().split('T')[0],
    autoclave_id: ''
  });
  const [expandedAutoclave, setExpandedAutoclave] = useState(null);

  useEffect(() => { fetchDados(); }, []);

  const fetchDados = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
      if (filters.data_fim) params.append('data_fim', filters.data_fim);
      if (filters.autoclave_id) params.append('autoclave_id', filters.autoclave_id);
      const response = await api.get(`/relatorios/ciclos-por-autoclave?${params.toString()}`);
      setDados(response.data);
    } catch (error) {
      toast.error('Erro ao carregar relatório de autoclaves');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleFilter = () => { fetchDados(); };

  const handlePrint = (autoclaveId) => {
    const autoclave = dados?.autoclaves?.find(a => a.autoclave_id === autoclaveId);
    if (!autoclave) return;

    const printWindow = window.open('', '_blank');
    const rows = autoclave.ciclos.map(c => `
      <tr>
        <td style="padding:6px 10px;border:1px solid #ddd;">${c.numero_ciclo || '-'}</td>
        <td style="padding:6px 10px;border:1px solid #ddd;">${formatDateTime(c.data_hora)}</td>
        <td style="padding:6px 10px;border:1px solid #ddd;">${c.hora_inicio || '-'} - ${c.hora_fim || '-'}</td>
        <td style="padding:6px 10px;border:1px solid #ddd;">${c.metodo || '-'}</td>
        <td style="padding:6px 10px;border:1px solid #ddd;">${c.temperatura || '-'}</td>
        <td style="padding:6px 10px;border:1px solid #ddd;">${c.pressao || '-'}</td>
        <td style="padding:6px 10px;border:1px solid #ddd;">${c.tempo_minutos || '-'} min</td>
        <td style="padding:6px 10px;border:1px solid #ddd;">${c.numero_biologico || '-'}</td>
        <td style="padding:6px 10px;border:1px solid #ddd;text-align:center;font-weight:bold;color:${c.resultado === 'aprovado' ? '#16a34a' : c.resultado === 'reprovado' ? '#dc2626' : '#f59e0b'}">${c.resultado === 'aprovado' ? 'Aprovado' : c.resultado === 'reprovado' ? 'Reprovado' : 'Pendente'}</td>
        <td style="padding:6px 10px;border:1px solid #ddd;">${c.is_bowie_dick ? 'Sim' : '-'}</td>
        <td style="padding:6px 10px;border:1px solid #ddd;">${c.clientes || '-'}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <html>
      <head>
        <title>Relatório - ${autoclave.identificacao}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
          h1 { font-size: 18px; margin-bottom: 4px; }
          h2 { font-size: 14px; color: #666; margin-bottom: 16px; }
          .stats { display: flex; gap: 24px; margin-bottom: 16px; }
          .stat { padding: 8px 16px; background: #f1f5f9; border-radius: 6px; }
          .stat b { display: block; font-size: 20px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th { background: #1e40af; color: white; padding: 8px 10px; text-align: left; }
          tr:nth-child(even) { background: #f8fafc; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <h1>Relatório de Ciclos - ${autoclave.identificacao}</h1>
        <h2>${autoclave.marca} ${autoclave.modelo} | Período: ${formatDate(dados.periodo.inicio)} a ${formatDate(dados.periodo.fim)}</h2>
        <div class="stats">
          <div class="stat">Total<b>${autoclave.total}</b></div>
          <div class="stat" style="color:#16a34a">Aprovados<b>${autoclave.aprovados}</b></div>
          <div class="stat" style="color:#dc2626">Reprovados<b>${autoclave.reprovados}</b></div>
          <div class="stat" style="color:#f59e0b">Pendentes<b>${autoclave.pendentes}</b></div>
          ${autoclave.requer_bowie_dick ? `<div class="stat" style="color:#7c3aed">Bowie-Dick<b>${autoclave.bowie_dick_realizados}</b></div>` : ''}
        </div>
        <table>
          <thead>
            <tr>
              <th>N. Ciclo</th><th>Data/Hora</th><th>Início/Fim</th><th>Método</th>
              <th>Temp.</th><th>Pressão</th><th>Tempo</th><th>N. Biológico</th>
              <th>Resultado</th><th>B-D</th><th>Clientes</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <p style="margin-top:16px;font-size:11px;color:#999;">Impresso em ${formatDateTime(new Date().toISOString())} | Sterisul CME</p>
      </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  const getResultadoBadge = (resultado) => {
    if (resultado === 'aprovado') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="w-3 h-3" />Aprovado</span>;
    if (resultado === 'reprovado') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"><XCircle className="w-3 h-3" />Reprovado</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"><Clock className="w-3 h-3" />Pendente</span>;
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Filter className="w-5 h-5 text-blue-600" />
          Filtros
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="ac_data_inicio">Data Início</Label>
            <Input id="ac_data_inicio" type="date" value={filters.data_inicio} onChange={(e) => setFilters({ ...filters, data_inicio: e.target.value })} className="mt-1" data-testid="autoclave-date-start" />
          </div>
          <div>
            <Label htmlFor="ac_data_fim">Data Fim</Label>
            <Input id="ac_data_fim" type="date" value={filters.data_fim} onChange={(e) => setFilters({ ...filters, data_fim: e.target.value })} className="mt-1" data-testid="autoclave-date-end" />
          </div>
          <div>
            <Label htmlFor="ac_autoclave">Autoclave (opcional)</Label>
            <select id="ac_autoclave" value={filters.autoclave_id} onChange={(e) => setFilters({ ...filters, autoclave_id: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="autoclave-select-filter">
              <option value="">Todas as autoclaves</option>
              {dados?.autoclaves_disponiveis?.map(a => (<option key={a.id} value={a.id}>{a.identificacao}</option>))}
            </select>
          </div>
          <div className="flex items-end">
            <Button onClick={handleFilter} className="w-full" data-testid="filter-autoclave-btn">
              <Search className="w-4 h-4 mr-2" />
              Filtrar
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {dados && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg"><ThermometerSun className="w-6 h-6 text-blue-600" /></div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Autoclaves</p>
                <p className="text-2xl font-bold text-slate-900">{dados.autoclaves?.length || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg"><FileText className="w-6 h-6 text-green-600" /></div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Total de Ciclos</p>
                <p className="text-2xl font-bold text-slate-900">{dados.total_geral || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-100 rounded-lg"><Calendar className="w-6 h-6 text-indigo-600" /></div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Período</p>
                <p className="text-base font-bold text-slate-900">{formatDate(dados.periodo?.inicio)} - {formatDate(dados.periodo?.fim)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Autoclave Cards */}
      {dados?.autoclaves?.map((autoclave) => (
        <div key={autoclave.autoclave_id} className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid={`autoclave-card-${autoclave.autoclave_id}`}>
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-600 rounded-lg">
                <ThermometerSun className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">{autoclave.identificacao}</h3>
                <p className="text-sm text-slate-500">{autoclave.marca} {autoclave.modelo}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-4 text-sm">
                <span className="font-semibold text-slate-700">{autoclave.total} ciclos</span>
                <span className="text-green-600 font-medium">{autoclave.aprovados} aprov.</span>
                <span className="text-red-600 font-medium">{autoclave.reprovados} reprov.</span>
                <span className="text-amber-600 font-medium">{autoclave.pendentes} pend.</span>
                {autoclave.requer_bowie_dick && (
                  <span className="text-purple-600 font-medium">{autoclave.bowie_dick_realizados} B-D</span>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => handlePrint(autoclave.autoclave_id)} data-testid={`print-autoclave-${autoclave.autoclave_id}`}>
                <Printer className="w-4 h-4 mr-1" />
                Imprimir
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpandedAutoclave(expandedAutoclave === autoclave.autoclave_id ? null : autoclave.autoclave_id)}
                data-testid={`toggle-autoclave-${autoclave.autoclave_id}`}
              >
                {expandedAutoclave === autoclave.autoclave_id ? 'Recolher' : 'Expandir'}
              </Button>
            </div>
          </div>

          {/* Approval Rate Bar */}
          <div className="px-6 py-2 bg-white border-b border-slate-100">
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500 w-28">Taxa Aprovação:</span>
              <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-green-500 rounded-full transition-all duration-500"
                  style={{ width: `${autoclave.total > 0 ? (autoclave.aprovados / autoclave.total * 100) : 0}%` }}
                />
              </div>
              <span className="text-sm font-bold text-slate-700 w-14 text-right">
                {autoclave.total > 0 ? (autoclave.aprovados / autoclave.total * 100).toFixed(1) : 0}%
              </span>
            </div>
          </div>

          {/* Expandable Table */}
          {expandedAutoclave === autoclave.autoclave_id && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">N. Ciclo</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Data/Hora</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Início/Fim</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Método</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Temp.</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Pressão</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Tempo</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">N. Biológico</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Resultado</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">B-D</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Clientes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {autoclave.ciclos.map((ciclo) => (
                    <tr key={ciclo.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">{ciclo.numero_ciclo || '-'}</td>
                      <td className="px-4 py-3 text-slate-600">{formatDateTime(ciclo.data_hora)}</td>
                      <td className="px-4 py-3 text-slate-600">{ciclo.hora_inicio || '-'} - {ciclo.hora_fim || '-'}</td>
                      <td className="px-4 py-3 text-slate-600">{ciclo.metodo || '-'}</td>
                      <td className="px-4 py-3 text-center text-slate-600">{ciclo.temperatura || '-'}</td>
                      <td className="px-4 py-3 text-center text-slate-600">{ciclo.pressao || '-'}</td>
                      <td className="px-4 py-3 text-center text-slate-600">{ciclo.tempo_minutos || '-'} min</td>
                      <td className="px-4 py-3 text-center text-slate-600">{ciclo.numero_biologico || '-'}</td>
                      <td className="px-4 py-3 text-center">{getResultadoBadge(ciclo.resultado)}</td>
                      <td className="px-4 py-3 text-center">{ciclo.is_bowie_dick ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Sim</span> : '-'}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate">{ciclo.clientes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      {/* Empty State */}
      {dados && (!dados.autoclaves || dados.autoclaves.length === 0) && (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <ThermometerSun className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-600">Nenhum ciclo encontrado</h3>
          <p className="text-sm text-slate-500 mt-2">Ajuste os filtros ou cadastre ciclos de esterilização</p>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}
    </div>
  );
}


// ======================== PÁGINA PRINCIPAL ========================
export default function RelatoriosPage() {
  return (
    <div data-testid="relatorios-page" className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Relatórios
        </h1>
        <p className="text-lg text-slate-600 mt-2">Visualização e exportação de relatórios operacionais</p>
      </div>

      <Tabs defaultValue="cobranca" className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="cobranca" className="data-[state=active]:bg-white" data-testid="tab-cobranca">
            <Users className="w-4 h-4 mr-2" />
            Cobrança
          </TabsTrigger>
          <TabsTrigger value="autoclaves" className="data-[state=active]:bg-white" data-testid="tab-autoclaves">
            <ThermometerSun className="w-4 h-4 mr-2" />
            Por Autoclave
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cobranca" className="mt-6">
          <CobrancaTab />
        </TabsContent>

        <TabsContent value="autoclaves" className="mt-6">
          <AutoclavesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
